import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import '../App.css';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from './config/apiConfig';
import { toast } from 'react-toastify';
import { marked } from 'marked'; 
import TextareaAutosize from 'react-textarea-autosize'; 
import DOMPurify from 'dompurify';

import Sidebar from './Sidebar';
import ProfilePopup from './ProfilePopup';
import SettingsScreen from './SettingsScreen';
import Modal from './Modal'; // Importar el nuevo componente modal
import ReferenceDocs from './ReferenceDocs';
import MessageActions from './MessageActions';
import LoadingSpinner from './LoadingSpinner';
import TaskProgress from './TaskProgress';
import { ChatMessage, HistoryItem, ApiTurn, Prompt, AssistantStreamingContent, Step } from '../types';

type StreamEventType = 'status' | 'log' | 'report' | 'error';

// --- COMPONENTE PRINCIPAL ---
const ChatScreen: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
    const { authData } = useAuth();
    const [currentView, setCurrentView] = useState<'chat' | 'settings'>('chat');
    
    const [currentMessage, setCurrentMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSidebarVisible, setSidebarVisible] = useState(false);
    const [isProfilePopupVisible, setProfilePopupVisible] = useState(false);
    const [activeChatId, setActiveChatId] = useState<string | null>(null);
    const [historyList, setHistoryList] = useState<HistoryItem[]>([]);
    const [fullHistoryData, setFullHistoryData] = useState<Record<string, ApiTurn[]>>({});
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [streamingContent, setStreamingContent] = useState<AssistantStreamingContent | null>(null);
    const messageAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const fetchAndParsePrompts = async () => {
        try {
          const response = await fetch('/prompts.xml'); // Fetch desde la carpeta public
          const xmlText = await response.text();
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlText, "application/xml");
          const items = xmlDoc.getElementsByTagName('Item');
          const allPrompts: Prompt[] = Array.from(items).map(item => ({
            id: item.querySelector('Id')?.textContent ?? '',
            title: item.querySelector('Titulo')?.textContent ?? '',
            content: item.querySelector('Contenido')?.textContent ?? '',
          }));
          // Tomamos solo los últimos 4
          setPrompts(allPrompts.slice(-4));
        } catch (error) {
          console.error("Error al cargar o parsear los prompts XML:", error);
        }
      };
      fetchAndParsePrompts();
    }, []); // El array vacío asegura que se ejecute solo una vez

    useEffect(() => {
      const baseTitle = process.env.REACT_APP_WEBSITE_TITLE || "Chat";
      document.title = baseTitle;
      if (authData.username) {
        fetchHistoryFromApi(authData.username);
      } else {
        onLogout();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData.username]); // Se ejecuta cuando el username del contexto está disponible

    useEffect(() => {
      if (messageAreaRef.current) {
        messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
      }
    }, [messages, streamingContent]);

    const fetchHistoryFromApi = async (user: string) => {
      setIsHistoryLoading(true);
      const token = authData.token; 
      if (!token) { onLogout(); return; }
      try {
        const response = await fetch(API_ENDPOINTS.getChatHistory(user), { headers: { 'Authorization': `Bearer ${token}` } });
        if (!response.ok) throw new Error('Error al obtener historial');
        const data = await response.json();
        const historyFromApi = data.history || {};
        const initialList: HistoryItem[] = Object.keys(historyFromApi)
          .map(chatId => ({ id: chatId, title: historyFromApi[chatId][0].question }))
          .sort((a,b) => new Date(historyFromApi[b.id][0].datetime).getTime() - new Date(historyFromApi[a.id][0].datetime).getTime());
        setHistoryList(initialList);
        setFullHistoryData(historyFromApi);
        setActiveChatId(null);
        setMessages([{ 
          role: 'assistant', 
          content: `<h2>Bienvenido, ${user}.</h2><p>Selecciona una conversación del historial o crea un nuevo chat para comenzar.</p>` 
        }]);
      } catch (error) {
          setActiveChatId(null);
          setMessages([{
              role: 'assistant',
              content: `<h2>Bienvenido, ${user}.</h2><p>No se pudo cargar tu historial. Puedes crear un nuevo chat.</p>`
          }]);
      } finally {
          setIsHistoryLoading(false);
      }
    };

    const handleSelectChat = (chatId: string) => {
      setActiveChatId(chatId);
      const conversation = fullHistoryData[chatId];
      if (!conversation) {
        setMessages([{role: 'assistant', content: 'Error: No se pudo cargar la conversación.'}]);
        return;
      }
      const newMessages: ChatMessage[] = [];
      conversation.forEach(turn => {
        newMessages.push({ role: 'user', content: turn.question });
        newMessages.push({ 
          role: 'assistant', 
          content: turn.answer, 
          docs: parseDocsFromString(turn.docs),
          isActionable: true // <-- LÍNEA CLAVE AÑADIDA
        });
      });
      setMessages(newMessages);
      if (isSidebarVisible) setSidebarVisible(false);
    };
    
  const handleNewChat = (currentUser: string) => {
    const newChatId = uuidv4();
    setActiveChatId(newChatId);
    setMessages([{ role: 'assistant', content: `¡Hola, ${currentUser}! ¿Listo para una nueva consulta?` }]);
    if (isSidebarVisible) setSidebarVisible(false);
  };

  const parseDocsFromString = (docsData: string | Record<string, string>): Record<string, string> => {
      if (typeof docsData === 'object' && docsData !== null) return docsData;
      if (typeof docsData !== 'string' || !docsData.trim()) return {};
      const docsObject: Record<string, string> = {};
      let entries: string[] = [];
      if (docsData.startsWith("{'") && docsData.endsWith("'}") ) {
        const innerContent = docsData.slice(2, -2);
        entries = innerContent.split("', '");
      } else {
        entries = docsData.split(/\n---\n/);
      }
      entries.forEach(entry => {
        const cleanedEntry = entry.trim().replace(/"/g, '').replace(/\\\\/g, '\\');
        if (cleanedEntry) docsObject[cleanedEntry] = '#';
      });
      return docsObject;
    };

  const parseLogContent = (logString: string): string => {
      const tupleRegex = /\('([^']*)',\s*'?(.*?)'?\)$/s;
      const match = logString.match(tupleRegex);
      if (match && match[1] && match[2]) {
        const key = match[1];
        let value = match[2];
        if (value.length > 250) {
          value = value.substring(0, 250) + "...'";
        }
        return `[${key}]: ${value}`;
      }
      return logString;
    };

  const parseAndStyleFinalResult = (rawContent: string): string => {
    const rawContentRegex = /\('raw', '(.*?)'\)/s;
    const match = rawContent.match(rawContentRegex);
    if (match && match[1]) {
      let markdownContent = match[1];
      markdownContent = markdownContent.replace(/\\n/g, '\n').replace(/\\u00d1/g, 'Ñ').replace(/\\u00f1/g, 'ñ');
      return marked.parse(markdownContent) as string;
    }
    
    return `<pre>${rawContent}</pre>`;
  };

  const handleRateResponse = async (rating: 'good' | 'bad', question: string, messageIndex: number): Promise<boolean> => {
    if (!authData.token || !authData.username || !activeChatId) { alert("No se puede calificar: Sesión o chat no activo."); return false; }
    try {
      
      const ratingVal = rating == "good" ? 1 : 0;
      const requestBody = { username: authData.username, 
                            chat_id: activeChatId, 
                            message_index: messageIndex, 
                            rating: ratingVal };
      console.info("requestBody: ",requestBody);                            
      const response = await fetch(API_ENDPOINTS.rateResponse, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.detail || "Error al enviar la calificación."); }
      return true;
    } catch (error: any) {
      alert(`Error: ${error.message}`);
      return false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };
  
  const toggleSidebar = () => setSidebarVisible(prev => !prev);

  const handlePromptClick = (prompt: Prompt) => {
    setModalTitle(prompt.title);
    setModalContent(prompt.content);
    setIsModalOpen(true);
  };

 const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading || !authData.token) {
      if (!authData.token) onLogout(); return;
    }
    let currentChatId = activeChatId;
    if (!currentChatId) {
      currentChatId = uuidv4();
      setActiveChatId(currentChatId);
      setMessages([]);
    }
    const userMessage: ChatMessage = { role: 'user', content: currentMessage };
    const topicToSend = currentMessage;
    setCurrentMessage('');
    const assistantMessageId = uuidv4();
    const initialContent: AssistantStreamingContent = { steps: [], logs: [], finalHTML: null };
    setMessages(prev => [...prev, userMessage, { id: assistantMessageId, role: 'assistant', content: initialContent, isActionable: false }]);
    setIsLoading(true);
    let finalAnswerForHistory: string | null = null;
    let finalDocsForHistory: any = null;
    try {
      const response = await fetch(API_ENDPOINTS.startMultiAgentChat, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ topic: topicToSend, username: authData.username, chat_id: currentChatId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'El servidor no pudo procesar la solicitud.');
      }
      if (!response.body) throw new Error("La respuesta no es un stream.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalAnswerForHistory: string | null = null;
      let finalDocsForHistory: any = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n');
        
        while (boundary !== -1) {
          const line = buffer.substring(0, boundary).trim();
          buffer = buffer.substring(boundary + 1);
          
          if (line.startsWith('data: ')) {
            const jsonString = line.substring(6);
            if (jsonString) {
              try {
                const eventData = JSON.parse(jsonString);
                
                const eventType = eventData.type;
                const eventContent = eventData.content;
                if (!eventType) {
                    console.warn("Evento de stream no reconocido:", eventData);
                    continue;
                }

                 setMessages(prev => {
                  const lastMessage = prev[prev.length - 1];
                  if (!lastMessage || lastMessage.id !== assistantMessageId || typeof lastMessage.content !== 'object') {
                    return prev;
                  }

                  const newContent = JSON.parse(JSON.stringify(lastMessage.content));
                  
                  // El switch ahora funciona porque 'eventType' tiene un valor
                  switch(eventType) {
                      case 'status':
                          if (newContent.steps.length > 0) newContent.steps[newContent.steps.length - 1].status = 'completed';
                          newContent.steps.push({ id: uuidv4(), content: eventContent, status: 'running' });
                          break;
                      case 'log':
                          if (newContent.steps.length > 0) newContent.steps[newContent.steps.length - 1].status = 'completed';
                          newContent.steps.push({ id: uuidv4(), content: eventContent, status: 'running' });
                          break;
                      case 'final_result': // Asumimos que el resultado final viene con este tipo
                            const sanitizedContent = eventContent.trim();
                            const plainHTML = DOMPurify.sanitize(sanitizedContent, {
                              ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li'], // solo etiquetas seguras
                              ALLOWED_ATTR: [] // sin atributos (elimina style, class, etc.)
                            });
                            finalAnswerForHistory = plainHTML;
                          //const styledHTML = parseAndStyleFinalResult(eventContent);
                          //finalAnswerForHistory = styledHTML;
                            newContent.finalHTML = plainHTML;
                            if (newContent.steps.length > 0) newContent.steps[newContent.steps.length - 1].status = 'completed';
                          break;
                      case 'error':
                          newContent.finalHTML = `<p class="error-message">${eventContent}</p>`;
                          toast.error(eventContent);
                          break;
                  }
                  
                  const updatedMessages = [...prev.slice(0, -1), { ...lastMessage, content: newContent }];
                  return updatedMessages;
                });

              } catch (e) { console.warn("No se pudo parsear el fragmento:", jsonString, e); }
            }
          }
          boundary = buffer.indexOf('\n');
        }
      }
      const finalChatId = currentChatId;
      if (finalChatId) {
        setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId ? { ...msg, isActionable: true } : msg
        ));
        const chatExistsInHistory = historyList.some(item => item.id === finalChatId);
        if (!chatExistsInHistory) {
          const newHistoryItem: HistoryItem = { id: finalChatId, title: topicToSend };
          setHistoryList(prev => [newHistoryItem, ...prev]);
        }

        if (finalAnswerForHistory) {
          const newTurn: ApiTurn = {
            datetime: new Date().toISOString().slice(0, -1),
            question: topicToSend,
            answer: finalAnswerForHistory,
            docs: finalDocsForHistory || {},
          };
          setFullHistoryData(prev => {
            // Usamos 'finalChatId', que TypeScript sabe que es un 'string'.
            // Todos los errores aquí desaparecen.
            const updatedConversation = [...(prev[finalChatId] || []), newTurn];
            return { ...prev, [finalChatId]: updatedConversation };
          });
        }
      }
    } catch (error: any) {
      toast.error(error.message);
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId && msg.content !== topicToSend));
    } finally {
      setIsLoading(false);
    }
  };

  // --- RENDERIZADO CONDICIONAL ---
  if (currentView === 'settings') {
    return <SettingsScreen onBackToChat={() => setCurrentView('chat')} />;
  }

  // --- RENDERIZADO ---
  return (
    <>
      <div className="chat-container">
        {isSidebarVisible && <div className="overlay visible" onClick={toggleSidebar}></div>}
        <Sidebar
          className={`sidebar ${isSidebarVisible ? 'visible' : ''}`}
          isHistoryLoading={isHistoryLoading} historyList={historyList} username={authData.username || '...'}
          onNewChat={() => handleNewChat(authData.username || 'usuario')} onSelectChat={handleSelectChat}
          onProfileClick={() => setProfilePopupVisible(!isProfilePopupVisible)}
        />
        <div className="main-content">
          <div className="hamburger-menu" onClick={toggleSidebar}>☰</div>
          <div className="message-area" ref={messageAreaRef}>
             {messages.map((msg, index) => (
                <div key={msg.id || index} className={`message ${msg.role}`}>
                  {typeof msg.content === 'string' ? (
                     <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                  ) : (
                    <>
                      <TaskProgress steps={msg.content.steps} logs={msg.content.logs} />
                      {msg.content.finalHTML && <div className="final-response" dangerouslySetInnerHTML={{ __html: msg.content.finalHTML }} />}
                    </>
                  )}
                  {msg.docs && Object.keys(msg.docs).length > 0 && <ReferenceDocs docs={msg.docs} />}
                  {msg.isActionable && (
                    <MessageActions
                      answerContent={typeof msg.content === 'object' ? msg.content.finalHTML || '' : msg.content}
                      onRate={(rating) => {
                        const questionText = messages[index - 1]?.content as string || "";
                        return handleRateResponse(rating, questionText, index - 1);
                      }}
                    />
                  )}
                </div>
              ))}
          </div>
          
          <div className="input-area-wrapper">
            {prompts.length > 0 && (
              <div className="prompt-buttons-container">
                {prompts.map(prompt => (
                  <button key={prompt.id} className="prompt-button" onClick={() => handlePromptClick(prompt)}>
                    <span>{prompt.title.split(' ')[0]}</span>
                    <span>{prompt.title.substring(prompt.title.indexOf(' ') + 1)}</span>
                  </button>
                ))}
              </div>
            )}
            
            <div className="input-area">
               <TextareaAutosize
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="¿Cómo puedo ayudarte hoy?"
                minRows={1}    // Empezará con la altura de una sola línea
                maxRows={6}    // Crecerá hasta un máximo de 6 líneas de altura
                disabled={isLoading || !activeChatId}
              />
              {isLoading ? (
                <div className="loading-spinner-button"><LoadingSpinner /></div>
              ) : (
                <button 
                  onClick={handleSendMessage} 
                  disabled={!currentMessage.trim() || !activeChatId} 
                  title="Enviar"
                >
                  ➤
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {isProfilePopupVisible && (
        <ProfilePopup 
          onClose={() => setProfilePopupVisible(false)}
          onLogout={onLogout}
          onGoToSettings={() => setCurrentView('settings')}
        />
      )}

      <Modal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
      >
        <div dangerouslySetInnerHTML={{ __html: modalContent }} />
      </Modal>
    </>
  );
};

export default ChatScreen;